import React from 'react';
import { 
  ArrowRight, Package, ShoppingCart, 
  TrendingUp, AlertTriangle, Clock, 
  Truck, BarChart, Calendar
} from 'lucide-react';
import { 
  mrpPlanStatuses, mrpItemStatuses, mrpPlans,
  billOfMaterials, purchaseSuggestions
} from '../../data/mrpData';
import { products, suppliers } from '../../data/inventoryData';

const MRPItemDetail = ({ item, setActiveView }) => {
  if (!item) return null;
  
  // Helper functions
  const getStatusDetails = (statusId, type = 'item') => {
    const statuses = type === 'plan' ? mrpPlanStatuses : mrpItemStatuses;
    return statuses.find(status => status.id === statusId) || {
      name: '未知',
      color: '#6B7280',
      icon: 'HelpCircle'
    };
  };
  
  const getProductDetails = (productId) => {
    return products.find(p => p.id === productId) || { name: '未知產品', sku: 'N/A' };
  };
  
  const getSupplierDetails = (supplierId) => {
    return suppliers.find(s => s.id === supplierId) || { name: '未知供應商' };
  };
  
  const getPlanDetails = (planId) => {
    return mrpPlans.find(p => p.id === planId) || { name: '未知計劃' };
  };
  
  // Get related data
  const product = getProductDetails(item.productId);
  const status = getStatusDetails(item.status);
  const plan = getPlanDetails(item.planId);
  
  // Get BOM for this product
  const productBom = billOfMaterials.filter(bom => bom.parentProductId === item.productId);
  
  // Get purchase suggestions for this item
  const itemSuggestions = purchaseSuggestions.filter(s => s.mrpItemId === item.id);
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">物料項目詳情</h2>
        <button 
          onClick={() => setActiveView('plan-detail')} 
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center"
        >
          <ArrowRight className="w-4 h-4 mr-2" />
          返回計劃詳情
        </button>
      </div>
      
      {/* Item Header */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{product.name}</h3>
              <p className="text-sm text-gray-500">SKU: {product.sku}</p>
              <span 
                className="mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                style={{ backgroundColor: `${status.color}20`, color: status.color }}
              >
                {status.name}
              </span>
            </div>
            
            <div className="flex flex-col items-end">
              <p className="text-sm text-gray-500">計劃: {plan.name}</p>
              <p className="text-sm text-gray-500">更新日期: {new Date(item.updatedAt).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Item Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <Package className="h-5 w-5 text-blue-600" />
                </div>
                <h4 className="font-medium text-gray-900">物料資訊</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">需求數量:</p>
                  <p className="text-sm font-medium text-gray-900">{item.requiredQuantity}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">可用數量:</p>
                  <p className="text-sm font-medium text-gray-900">{item.availableQuantity}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">淨需求:</p>
                  <p className="text-sm font-medium text-gray-900">{item.netRequirement}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">計劃訂單:</p>
                  <p className="text-sm font-medium text-gray-900">{item.plannedOrderQuantity}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">優先級:</p>
                  <p className="text-sm font-medium">
                    <span 
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item.priority === 'critical' ? 'bg-red-100 text-red-800' : item.priority === 'high' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}
                    >
                      {item.priority === 'critical' ? '緊急' : item.priority === 'high' ? '高' : '普通'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            
            {/* Item Timeline */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-4">
                <div className="bg-green-100 p-2 rounded-full mr-3">
                  <Calendar className="h-5 w-5 text-green-600" />
                </div>
                <h4 className="font-medium text-gray-900">時間資訊</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">需求日期:</p>
                  <p className="text-sm font-medium text-gray-900">{item.requiredDate}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">計劃訂單釋放日期:</p>
                  <p className="text-sm font-medium text-gray-900">{item.plannedOrderReleaseDate}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">計劃訂單完成日期:</p>
                  <p className="text-sm font-medium text-gray-900">{item.plannedOrderDueDate}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">前置時間:</p>
                  <p className="text-sm font-medium text-gray-900">{item.leadTime} 天</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Product Image */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">產品圖片</h3>
        </div>
        <div className="p-6">
          <div className="flex justify-center">
            <img 
              src={product.image || "https://images.unsplash.com/photo-1581092921461-7edd2bec4d9e?w=800&h=400&fit=crop"} 
              alt={product.name} 
              className="rounded-lg shadow-sm max-w-full h-auto max-h-64"
            />
          </div>
        </div>
      </div>
      
      {/* Bill of Materials */}
      {productBom.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">物料清單 (BOM)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">組件</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">數量</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">單位</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">備註</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {productBom.map((bom) => {
                  const component = getProductDetails(bom.childProductId);
                  return (
                    <tr 
                      key={bom.id} 
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{component.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bom.quantity}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bom.unit}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bom.notes || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Purchase Suggestions */}
      {itemSuggestions.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">採購建議</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">建議數量</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">建議訂購日期</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">建議供應商</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">緊急程度</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {itemSuggestions.map((suggestion) => {
                  const supplier = getSupplierDetails(suggestion.suggestedSupplierId);
                  return (
                    <tr 
                      key={suggestion.id} 
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{suggestion.suggestedQuantity}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{suggestion.suggestedOrderDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{supplier.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span 
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${suggestion.urgency === 'high' ? 'bg-red-100 text-red-800' : suggestion.urgency === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}
                        >
                          {suggestion.urgency === 'high' ? '高' : suggestion.urgency === 'medium' ? '中' : '低'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{suggestion.status === 'pending' ? '待處理' : suggestion.status === 'approved' ? '已核准' : '已拒絕'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MRPItemDetail;
